<template>
	<article
		class="wg"
		:class="{
			'wg--dragging': dragging,
			'wg--over': dragOver && dragActive && !dragging,
			'wg--drop-before': dragOver && dropSide === 'before',
			'wg--drop-after': dragOver && dropSide === 'after'
		}"
		:style="placementStyle"
		@dragover.prevent="handleDragOver"
		@dragleave="handleDragLeave"
		@drop.prevent.stop="handleDrop"
	>
		<header
			class="wg__head"
			:class="{'wg__head--grabbable': editMode}"
			:draggable="editMode"
			title="按住分组栏拖动可调整位置"
			@dragstart="handleWidgetDragStart"
			@dragend="$emit('drag-end')"
		>
			<span v-if="editMode" class="wg__handle" aria-hidden="true">⠿</span>
			<span class="wg__icon">
				<img v-if="isImageIcon" :src="category.icon" alt="">
				<template v-else>{{ category.icon || '📁' }}</template>
			</span>
			<h2 class="wg__name" :title="category.name">{{ category.name }}</h2>
			<span class="wg__count">{{ total }}</span>
			<div class="wg__tools">
				<button v-if="editMode" type="button" class="wg__tool wg__tool--add" title="添加书签" @click="$emit('add-bookmark', category)">＋</button>
				<button v-if="editMode" type="button" class="wg__tool wg__tool--size" :title="sizeTitle" @click="$emit('resize', category)">{{ sizeLabel }}</button>
				<button v-if="editMode" type="button" class="wg__tool wg__tool--edit" title="编辑分组" @click="$emit('edit-category', category)">✎</button>
				<button v-if="editMode" type="button" class="wg__tool wg__tool--remove" title="删除分组" @click="$emit('remove-category', category)">✕</button>
				<button v-if="overflowCount > 0" type="button" class="wg__expand" title="查看全部书签" @click="$emit('expand', category)">＋{{ overflowCount }}</button>
			</div>
		</header>

		<div ref="body" class="wg__body">
			<div class="wg__grid" :style="gridStyle">
				<BookmarkCard
					v-for="bookmark in visibleBookmarks"
					:key="bookmark.id"
					:bookmark="bookmark"
					:edit-mode="editMode"
					:dragging="Boolean(draggingBookmark && draggingBookmark.id === bookmark.id)"
					@open="$emit('open', $event)"
					@edit="$emit('edit-bookmark', $event)"
					@remove="$emit('remove-bookmark', $event)"
					@drag-start="$emit('card-drag-start', $event)"
					@drop="handleCardDrop"
					@drag-end="$emit('drag-end')"
				/>
				<button v-if="overflowCount > 0" type="button" class="wg__more" title="查看全部书签" @click="$emit('expand', category)">
					<span class="wg__more-count">+{{ overflowCount }}</span>
					<span class="wg__more-text">更多</span>
				</button>
			</div>
			<p v-if="!bookmarks.length" class="wg__empty">{{ editMode ? '点击右上角 ＋ 添加书签' : '暂无书签' }}</p>
		</div>
	</article>
</template>

<script>
	import BookmarkCard from './BookmarkCard'

	/**
	 * 分组尺寸选项及循环顺序
	 */
	const SIZE_OPTIONS = [
		{key: 'small', label: '小'},
		{key: 'wide', label: '宽'},
		{key: 'tall', label: '高'},
		{key: 'large', label: '大'}
	]

	/**
	 * 书签卡片最小宽度（px），用于计算一行能放几张卡
	 */
	const CARD_MIN_WIDTH = 68

	/**
	 * 浏览模式下的卡片行高（px）
	 */
	const CARD_ROW_HEIGHT = 84

	/**
	 * 编辑模式下的卡片行高（px），底部需要容纳编辑/删除操作栏
	 */
	const CARD_EDIT_ROW_HEIGHT = 106

	/**
	 * 卡片间距（px）
	 */
	const CARD_GAP = 10

	export default {
		name: 'CategoryWidget',
		components: {BookmarkCard},
		props: {
			/**
			 * 分组数据
			 */
			category: {
				type: Object,
				required: true
			},
			/**
			 * 当前展示的书签列表（可能已被关键词过滤）
			 */
			bookmarks: {
				type: Array,
				default: () => []
			},
			/**
			 * 分组内书签总数
			 */
			total: {
				type: Number,
				default: 0
			},
			/**
			 * 是否处于编辑模式
			 */
			editMode: {
				type: Boolean,
				default: false
			},
			/**
			 * 仪表盘中的网格位置 {c, r, w, h}
			 */
			placement: {
				type: Object,
				required: true
			},
			/**
			 * 当前分组是否正被拖拽
			 */
			dragging: {
				type: Boolean,
				default: false
			},
			/**
			 * 页面中是否存在任意拖拽（用于高亮可放置分组）
			 */
			dragActive: {
				type: Boolean,
				default: false
			},
			/**
			 * 正被拖拽的书签（用于标记半透明卡片）
			 */
			draggingBookmark: {
				type: Object,
				default: null
			}
		},
		data() {
			return {
				cols: 4,
				capacity: 8,
				dragOver: false,
				dropSide: '',
				observer: null
			}
		},
		computed: {
			/**
			 * 网格定位样式
			 */
			placementStyle() {
				const placement = this.placement || {c: 1, r: 1, w: 1, h: 1}
				return {
					gridColumn: `${placement.c} / span ${placement.w}`,
					gridRow: `${placement.r} / span ${placement.h}`
				}
			},
			/**
			 * 分组图标是否为图片地址
			 */
			isImageIcon() {
				return /^(http|data):/i.test((this.category.icon || '').trim())
			},
			/**
			 * 超出容量、被收进弹窗的书签数量
			 */
			overflowCount() {
				return Math.max(0, this.bookmarks.length - this.capacity)
			},
			/**
			 * 卡片区实际展示的书签（溢出时保留一格给「更多」入口）
			 */
			visibleBookmarks() {
				if (this.overflowCount > 0) {
					return this.bookmarks.slice(0, Math.max(0, this.capacity - 1))
				}
				return this.bookmarks
			},
			/**
			 * 当前模式下的卡片行高：编辑模式底部有操作栏，行高更大
			 */
			cardRowHeight() {
				return this.editMode ? CARD_EDIT_ROW_HEIGHT : CARD_ROW_HEIGHT
			},
			/**
			 * 卡片网格样式
			 */
			gridStyle() {
				return {
					gridTemplateColumns: `repeat(${this.cols}, 1fr)`,
					gridAutoRows: `${this.cardRowHeight}px`,
					gap: `${CARD_GAP}px`
				}
			},
			/**
			 * 当前尺寸的展示文案
			 */
			sizeLabel() {
				const option = SIZE_OPTIONS.find(item => item.key === (this.category.size || 'small'))
				return option ? option.label : '小'
			},
			/**
			 * 尺寸切换按钮提示
			 */
			sizeTitle() {
				return `分组尺寸：${this.sizeLabel}（点击切换）`
			}
		},
		mounted() {
			this.setupObserver()
		},
		beforeDestroy() {
			this.teardownObserver()
		},
		watch: {
			/**
			 * 切换编辑模式时行高变化，需要重算容量
			 */
			editMode() {
				this.recompute()
			}
		},
		methods: {
			/**
			 * 监听卡片区域尺寸变化，重算可容纳的卡片数量
			 */
			setupObserver() {
				if (window.ResizeObserver) {
					this.observer = new ResizeObserver(() => this.recompute())
					this.observer.observe(this.$refs.body)
				}
				this.recompute()
			},
			/**
			 * 销毁尺寸监听
			 */
			teardownObserver() {
				if (this.observer) {
					this.observer.disconnect()
					this.observer = null
				}
			},
			/**
			 * 依据卡片区域实际宽高计算行数与容量
			 */
			recompute() {
				const body = this.$refs.body
				if (!body) {
					return
				}
				const width = body.clientWidth
				const height = body.clientHeight
				this.cols = Math.max(1, Math.floor((width + CARD_GAP) / (CARD_MIN_WIDTH + CARD_GAP)))
				const rows = Math.max(1, Math.floor((height + CARD_GAP) / (this.cardRowHeight + CARD_GAP)))
				this.capacity = this.cols * rows
			},
			/**
			 * 拖拽悬停：记录高亮并计算插入方向
			 *
			 * @param {DragEvent} event 拖拽事件
			 */
			handleDragOver(event) {
				this.dragOver = true
				this.dropSide = this.computeDropSide(event)
			},
			/**
			 * 计算指针相对分组的前后方向，决定插入位置
			 *
			 * <p>横向分组（宽大于高）按左右半区判断，纵向分组（窄屏堆叠）按上下半区判断。</p>
			 *
			 * @param {DragEvent} event 拖拽事件
			 * @returns {String} 'before' 插入到前方 / 'after' 插入到后方
			 */
			computeDropSide(event) {
				const rect = this.$el.getBoundingClientRect()
				if (rect.width >= rect.height) {
					return event.clientX > rect.left + rect.width / 2 ? 'after' : 'before'
				}
				return event.clientY > rect.top + rect.height / 2 ? 'after' : 'before'
			},
			/**
			 * 拖拽离开分组时取消高亮
			 *
			 * @param {DragEvent} event 拖拽事件
			 */
			handleDragLeave(event) {
				if (!event.relatedTarget || !this.$el.contains(event.relatedTarget)) {
					this.dragOver = false
					this.dropSide = ''
				}
			},
			/**
			 * 拖拽物放置到分组，携带指针方向用于定位插入位置
			 *
			 * @param {DragEvent} event 拖拽事件
			 */
			handleDrop(event) {
				this.dragOver = false
				const insertAfter = this.computeDropSide(event) === 'after'
				this.dropSide = ''
				this.$emit('body-drop', {category: this.category, insertAfter})
			},
			/**
			 * 拖拽物放置到某张卡片上
			 *
			 * @param {Object} target 目标书签
			 * @param {DragEvent} event 拖拽事件
			 */
			handleCardDrop(target, event) {
				this.dragOver = false
				const insertAfter = this.computeDropSide(event) === 'after'
				this.dropSide = ''
				this.$emit('card-drop', {target, category: this.category, insertAfter})
			},
			/**
			 * 开始拖拽分组
			 *
			 * <p>标题栏整体可拖，但从按钮上发起的拖拽会被忽略，避免与按钮点击冲突。</p>
			 *
			 * @param {DragEvent} event 拖拽事件
			 */
			handleWidgetDragStart(event) {
				if (!this.editMode) {
					return
				}
				if (event.target && event.target.closest && event.target.closest('button')) {
					event.preventDefault()
					return
				}
				if (event.dataTransfer) {
					event.dataTransfer.effectAllowed = 'move'
					event.dataTransfer.setData('text/plain', String(this.category.id))
				}
				this.$emit('widget-drag-start', this.category)
			}
		}
	}
</script>

<style scoped>
	.wg {
		position: relative;
		display: flex;
		flex-direction: column;
		min-width: 0;
		min-height: 0;
		padding: 14px 16px 16px;
		border-radius: 22px;
		background: linear-gradient(180deg, rgba(255, 255, 255, 0.085), rgba(255, 255, 255, 0.042));
		border: 1px solid rgba(255, 255, 255, 0.1);
		box-shadow: 0 22px 48px rgba(3, 6, 22, 0.42);
		backdrop-filter: blur(18px);
		overflow: hidden;
		animation: wg-rise 0.55s cubic-bezier(0.22, 0.68, 0.36, 1) both;
		transition: border-color 0.25s ease, box-shadow 0.25s ease, background 0.25s ease, opacity 0.2s ease, transform 0.2s ease;
	}

	@keyframes wg-rise {
		from {
			opacity: 0;
			transform: translateY(18px);
		}
		to {
			opacity: 1;
			transform: translateY(0);
		}
	}

	.wg:hover {
		border-color: rgba(140, 164, 255, 0.22);
		box-shadow: 0 30px 60px rgba(3, 6, 22, 0.5);
	}

	.wg--over {
		border-color: rgba(140, 164, 255, 0.6);
		background: linear-gradient(180deg, rgba(120, 150, 255, 0.18), rgba(255, 255, 255, 0.06));
		box-shadow: 0 0 0 2px rgba(120, 150, 255, 0.28) inset, 0 26px 56px rgba(3, 6, 22, 0.5);
	}

	.wg--dragging {
		opacity: 0.45;
		transform: scale(0.97);
	}

	.wg--drop-before::after,
	.wg--drop-after::after {
		content: '';
		position: absolute;
		top: 12px;
		bottom: 12px;
		width: 4px;
		border-radius: 4px;
		background: linear-gradient(180deg, #6d7dff, #b06bff);
		box-shadow: 0 0 16px rgba(120, 150, 255, 0.8);
		pointer-events: none;
	}

	.wg--drop-before::after {
		left: 6px;
	}

	.wg--drop-after::after {
		right: 6px;
	}

	@media (max-width: 640px) {
		.wg--drop-before::after,
		.wg--drop-after::after {
			top: auto;
			bottom: auto;
			left: 12px;
			right: 12px;
			width: auto;
			height: 4px;
		}

		.wg--drop-before::after {
			top: 6px;
		}

		.wg--drop-after::after {
			bottom: 6px;
		}
	}

	.wg__head {
		display: flex;
		align-items: center;
		gap: 8px;
		flex: none;
		margin-bottom: 10px;
		min-width: 0;
	}

	.wg__head--grabbable {
		cursor: grab;
	}

	.wg__head--grabbable:active {
		cursor: grabbing;
	}

	.wg__handle {
		flex: none;
		color: rgba(190, 202, 240, 0.45);
		cursor: grab;
		font-size: 15px;
		line-height: 1;
	}

	.wg__icon {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		flex: none;
		width: 26px;
		height: 26px;
		border-radius: 9px;
		background: linear-gradient(135deg, rgba(120, 150, 255, 0.26), rgba(190, 110, 255, 0.26));
		box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.18);
		color: #dfe5ff;
		font-size: 14px;
	}

	.wg__icon img {
		width: 18px;
		height: 18px;
		object-fit: contain;
		border-radius: 4px;
	}

	.wg__name {
		flex: 1;
		min-width: 0;
		margin: 0;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		color: #eef2ff;
		font-size: 14px;
		font-weight: 600;
		letter-spacing: 0.5px;
	}

	.wg__count {
		flex: none;
		padding: 2px 8px;
		border-radius: 999px;
		background: rgba(255, 255, 255, 0.1);
		color: rgba(206, 216, 250, 0.7);
		font-size: 11px;
	}

	.wg__tools {
		display: flex;
		align-items: center;
		gap: 5px;
		flex: none;
	}

	.wg__tool {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 24px;
		height: 24px;
		padding: 0;
		border: 1px solid rgba(255, 255, 255, 0.2);
		border-radius: 8px;
		background: rgba(255, 255, 255, 0.09);
		color: rgba(232, 238, 255, 0.85);
		cursor: pointer;
		font-size: 12px;
		line-height: 1;
		transition: background 0.2s ease, border-color 0.2s ease;
	}

	.wg__tool--add,
	.wg__tool--edit {
		border-color: rgba(120, 150, 255, 0.45);
		background: rgba(109, 125, 255, 0.24);
		color: #cfd8ff;
	}

	.wg__tool--add:hover,
	.wg__tool--edit:hover {
		background: rgba(109, 125, 255, 0.44);
		color: #fff;
	}

	.wg__tool--size {
		width: auto;
		padding: 0 8px;
		font-size: 11px;
		border-color: rgba(190, 130, 255, 0.45);
		background: rgba(176, 107, 255, 0.22);
		color: #e3d2ff;
	}

	.wg__tool--size:hover {
		background: rgba(176, 107, 255, 0.4);
		color: #fff;
	}

	.wg__tool--remove {
		border-color: rgba(255, 120, 145, 0.45);
		background: rgba(255, 80, 110, 0.2);
		color: #ffc9d3;
	}

	.wg__tool--remove:hover {
		background: rgba(240, 80, 110, 0.42);
		color: #fff;
	}

	.wg__expand {
		padding: 3px 11px;
		border: none;
		border-radius: 999px;
		background: linear-gradient(135deg, rgba(109, 125, 255, 0.85), rgba(176, 107, 255, 0.85));
		color: #fff;
		cursor: pointer;
		font-size: 11px;
		font-weight: 500;
		line-height: 1.4;
		box-shadow: 0 8px 18px rgba(109, 125, 255, 0.32);
		transition: box-shadow 0.2s ease, transform 0.2s ease;
	}

	.wg__expand:hover {
		transform: translateY(-1px);
		box-shadow: 0 10px 22px rgba(109, 125, 255, 0.44);
	}

	.wg__body {
		position: relative;
		flex: 1;
		min-height: 0;
	}

	.wg__grid {
		display: grid;
	}

	.wg__more {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 4px;
		border: 1px dashed rgba(255, 255, 255, 0.24);
		border-radius: 18px;
		background: rgba(255, 255, 255, 0.035);
		color: rgba(200, 212, 248, 0.6);
		cursor: pointer;
		font-size: 11px;
		transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
	}

	.wg__more:hover {
		border-color: rgba(140, 164, 255, 0.55);
		background: rgba(140, 164, 255, 0.1);
		color: #c3cfff;
	}

	.wg__more-count {
		font-size: 16px;
		font-weight: 600;
	}

	.wg__more-text {
		letter-spacing: 1px;
	}

	.wg__empty {
		position: absolute;
		inset: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		margin: 0;
		color: rgba(180, 192, 230, 0.42);
		font-size: 12px;
	}

	.wg__grid >>> .bm-card__main {
		padding: 8px 4px 4px;
	}

	.wg__grid >>> .bm-card__icon {
		width: 38px;
		height: 38px;
		margin-bottom: 6px;
		border-radius: 12px;
	}

	.wg__grid >>> .bm-card__icon img {
		width: 20px;
		height: 20px;
	}

	.wg__grid >>> .bm-card__emoji {
		font-size: 20px;
	}

	.wg__grid >>> .bm-card__title {
		font-size: 12px;
		-webkit-line-clamp: 1;
		line-clamp: 1;
	}

	.wg__grid >>> .bm-card__actions {
		gap: 4px;
		padding: 0 6px 6px;
	}

	.wg__grid >>> .bm-card__action {
		height: 26px;
		border-radius: 8px;
		font-size: 11px;
	}

	.wg__grid >>> .bm-card__action-text {
		display: none;
	}

	@media (prefers-reduced-motion: reduce) {
		.wg {
			animation: none;
		}
	}
</style>
